// /src/hooks/useGuestAccessRequestsSocket.js

import { useCallback, useEffect, useRef } from 'react';
import { guestAccessRequestsSocket } from '../../utils/Socket';
import { resumeAll, registerSocket } from '../wsRecovery/wsResume';
import { setResumePointer, updateResumeSeq } from '../wsRecovery/wsResumeStore';
import { startHeartbeat, stopHeartbeat } from '../wsRecovery/heartbeat';
import {
  GUEST_ACCESS_WS_CHANNEL,
  guestAccessWsLastSeen,
  getGuestAccessWsDiff,
  wsResumeGuestAccessRequests,
} from '../pages/guestAccess/guestAccessApi';
import {
  fetchPendingGuestAccessCountOnce,
  invalidateGuestAccessPendingList,
  setPendingGuestAccessCount,
} from '../pages/guestAccess/guestAccessPendingStore';

const RESUME_REGISTRY_KEY = 'guestAccessRequests';

function applyPendingFromPayload(payload) {
  if (payload && typeof payload.pendingCount === 'number') {
    setPendingGuestAccessCount(payload.pendingCount);
    return true;
  }
  return false;
}

function seqSafe(n) {
  return Number.isFinite(n) ? n : 0;
}

/**
 * @param {{
 *   enabled?: boolean,
 *   isLoggedIn?: boolean,
 *   mode?: 'admin'|'self',
 *   selfUserId?: number|null,
 *   onSelfUpdate?: (payload: object) => void,
 *   onDatasetUpdate?: (payload: object) => void,
 * }} opts
 */
export default function useGuestAccessRequestsSocket({
  enabled = true,
  isLoggedIn = true,
  mode = 'admin',
  selfUserId = null,
  onSelfUpdate = null,
  onDatasetUpdate = null,
} = {}) {
  const lastResumeIdRef = useRef(0);
  const onSelfUpdateRef = useRef(onSelfUpdate);
  const onDatasetUpdateRef = useRef(onDatasetUpdate);
  const selfUserIdRef = useRef(selfUserId);
  onSelfUpdateRef.current = onSelfUpdate;
  onDatasetUpdateRef.current = onDatasetUpdate;
  selfUserIdRef.current = selfUserId;

  const syncPointer = useCallback(async (wsLogId) => {
    const seq = Number(wsLogId || 0);
    if (seq > 0) {
      await guestAccessWsLastSeen(seq).catch(() => {});
      lastResumeIdRef.current = Math.max(lastResumeIdRef.current, seq);
      updateResumeSeq(GUEST_ACCESS_WS_CHANNEL, seq);
    }
  }, []);

  const emitSelfRelevant = useCallback((payload) => {
    if (typeof onDatasetUpdateRef.current === 'function') {
      onDatasetUpdateRef.current(payload || {});
    }

    if (mode !== 'self' || typeof onSelfUpdateRef.current !== 'function') return;

    const expected = selfUserIdRef.current != null ? Number(selfUserIdRef.current) : null;
    const payloadUserId = payload?.userId != null ? Number(payload.userId) : null;
    // Live room emits are already scoped; resume batches may include other users.
    if (
      expected != null
      && Number.isFinite(expected)
      && payloadUserId != null
      && Number.isFinite(payloadUserId)
      && payloadUserId !== expected
    ) {
      return;
    }
    onSelfUpdateRef.current(payload || {});
  }, [mode]);

  const handleWsPayload = useCallback(
    async (payload, { invalidateList = true } = {}) => {
      const wsLogId = Number(payload?.wsLogId || 0);
      if (wsLogId > 0) {
        lastResumeIdRef.current = Math.max(lastResumeIdRef.current, seqSafe(wsLogId));
      }

      if (mode === 'admin') {
        if (!applyPendingFromPayload(payload)) {
          await fetchPendingGuestAccessCountOnce();
        }
        if (invalidateList) invalidateGuestAccessPendingList();
      }

      emitSelfRelevant(payload);

      if (wsLogId > 0) {
        await syncPointer(wsLogId);
      }
    },
    [mode, syncPointer, emitSelfRelevant],
  );

  const runWsResume = useCallback(async () => {
    try {
      const data = await wsResumeGuestAccessRequests(lastResumeIdRef.current);
      if (!data?.ok || !data.events?.length) return;

      const maxSeq = Math.max(...data.events.map((e) => Number(e.seq) || 0), 0);

      if (mode === 'admin') {
        const lastEvent = data.events[data.events.length - 1];
        const pendingFromMeta = lastEvent?.meta?.pendingCount;
        if (typeof pendingFromMeta === 'number') {
          setPendingGuestAccessCount(pendingFromMeta);
        } else {
          await fetchPendingGuestAccessCountOnce();
        }
        invalidateGuestAccessPendingList();
      }

      const expected = selfUserIdRef.current != null ? Number(selfUserIdRef.current) : null;
      const relevant = data.events
        .map((e) => e?.meta || {})
        .filter((meta) => {
          if (mode !== 'self') return true;
          if (expected == null || !Number.isFinite(expected)) return true;
          const uid = meta?.userId != null ? Number(meta.userId) : null;
          return uid === expected;
        });

      if (relevant.length > 0) {
        // Process chronologically so acceptance wins over older pending/create events.
        for (let i = 0; i < relevant.length; i += 1) {
          emitSelfRelevant(relevant[i]);
        }
      }

      if (maxSeq > 0) await syncPointer(maxSeq);
    } catch (err) {
      console.warn('[guestAccess WS] resume failed', err);
    }
  }, [mode, syncPointer, emitSelfRelevant]);

  useEffect(() => {
    if (!enabled || !isLoggedIn) return undefined;

    setResumePointer(GUEST_ACCESS_WS_CHANNEL, {});
    registerSocket(RESUME_REGISTRY_KEY, async () => {
      await runWsResume();
    });

    if (!guestAccessRequestsSocket.connected) {
      guestAccessRequestsSocket.connect();
      startHeartbeat(guestAccessRequestsSocket);
    }

    const join = () => {
      if (mode === 'admin') {
        guestAccessRequestsSocket.emit('joinGuestAccessAdmin');
      } else {
        guestAccessRequestsSocket.emit('joinGuestAccessSelf');
      }
    };

    const onConnect = () => {
      join();
      resumeAll();
    };

    const onReconnect = async () => {
      join();
      try {
        const diff = await getGuestAccessWsDiff();
        if (diff?.needRefresh) await runWsResume();
      } catch {
        // ignore
      }
    };

    const onMissed = async () => {
      // Missed payload is only a pointer hint — resume carries the real events.
      await runWsResume();
    };

    const onUpdate = async (payload) => {
      await handleWsPayload(payload, { invalidateList: true });
    };

    join();
    guestAccessRequestsSocket.on('connect', onConnect);
    guestAccessRequestsSocket.on('reconnect', onReconnect);
    guestAccessRequestsSocket.on('guestAccessRequestsMissed', onMissed);
    guestAccessRequestsSocket.on('guestAccessRequestsUpdate', onUpdate);

    if (mode === 'admin') {
      void fetchPendingGuestAccessCountOnce();
    } else {
      void runWsResume();
    }

    return () => {
      guestAccessRequestsSocket.off('connect', onConnect);
      guestAccessRequestsSocket.off('reconnect', onReconnect);
      guestAccessRequestsSocket.off('guestAccessRequestsMissed', onMissed);
      guestAccessRequestsSocket.off('guestAccessRequestsUpdate', onUpdate);
      if (mode === 'admin') {
        guestAccessRequestsSocket.emit('leaveGuestAccessAdmin');
      } else {
        guestAccessRequestsSocket.emit('leaveGuestAccessSelf');
      }
      // Do not stopHeartbeat here: admin + self hooks share one socket.
    };
  }, [enabled, isLoggedIn, mode, handleWsPayload, runWsResume]);
}
